// SPDX-License-Identifier: AGPL-3.0-or-later

import {areOrderedStringArraysEqual} from '@app/features/voice/utils/StringArrayUtils';
import {assign, getInitialSnapshot, type SnapshotFrom, setup, transition} from 'xstate';

interface VoiceStreamWatchContext {
	viewerStreamKeys: ReadonlyArray<string>;
	deferredStopKeys: ReadonlySet<string>;
}

interface VoiceStreamWatchInput {
	viewerStreamKeys?: ReadonlyArray<string> | null;
	deferredStopKeys?: Iterable<string> | null;
}

export type VoiceStreamWatchEvent =
	| {type: 'watch.replace'; keys: ReadonlyArray<string> | null | undefined}
	| {type: 'watch.add'; key: string}
	| {type: 'watch.remove'; key: string}
	| {type: 'watch.removeMany'; keys: ReadonlyArray<string>}
	| {type: 'watch.deferRemove'; key: string}
	| {type: 'watch.cancelDeferredRemove'; key: string}
	| {type: 'watch.reset'};

export function normalizeViewerStreamKeys(keys: ReadonlyArray<string> | null | undefined): Array<string> {
	if (!keys || keys.length === 0) return [];
	const normalized: Array<string> = [];
	const seen = new Set<string>();
	for (const key of keys) {
		if (!key || seen.has(key)) continue;
		seen.add(key);
		normalized.push(key);
	}
	return normalized;
}

function normalizeDeferredStopKeys(
	deferredStopKeys: Iterable<string> | null | undefined,
	viewerStreamKeys: ReadonlyArray<string>,
): ReadonlySet<string> {
	if (!deferredStopKeys) return EMPTY_SET;
	const viewerSet = new Set(viewerStreamKeys);
	const normalized = new Set<string>();
	for (const key of deferredStopKeys) {
		if (viewerSet.has(key)) normalized.add(key);
	}
	return normalized.size === 0 ? EMPTY_SET : normalized;
}

function setsEqual(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
	if (left === right) return true;
	if (left.size !== right.size) return false;
	for (const key of left) {
		if (!right.has(key)) return false;
	}
	return true;
}

function removeManyFromKeys(keys: ReadonlyArray<string>, removedKeys: ReadonlySet<string>): Array<string> {
	if (removedKeys.size === 0) return [...keys];
	return keys.filter((key) => !removedKeys.has(key));
}

function removeManyFromSet(keys: ReadonlySet<string>, removedKeys: ReadonlySet<string>): ReadonlySet<string> {
	if (keys.size === 0 || removedKeys.size === 0) return keys;
	const next = new Set<string>();
	for (const key of keys) {
		if (!removedKeys.has(key)) next.add(key);
	}
	return next.size === 0 ? EMPTY_SET : next;
}

function replaceContext(
	context: VoiceStreamWatchContext,
	keys: ReadonlyArray<string> | null | undefined,
): VoiceStreamWatchContext {
	const viewerStreamKeys = normalizeViewerStreamKeys(keys);
	const deferredStopKeys = normalizeDeferredStopKeys(context.deferredStopKeys, viewerStreamKeys);
	if (
		areOrderedStringArraysEqual(context.viewerStreamKeys, viewerStreamKeys) &&
		setsEqual(context.deferredStopKeys, deferredStopKeys)
	) {
		return context;
	}
	return {viewerStreamKeys, deferredStopKeys};
}

function addKey(context: VoiceStreamWatchContext, key: string): VoiceStreamWatchContext {
	if (!key || context.viewerStreamKeys.includes(key)) return context;
	return {
		viewerStreamKeys: [...context.viewerStreamKeys, key],
		deferredStopKeys: context.deferredStopKeys,
	};
}

function removeKeys(context: VoiceStreamWatchContext, keys: ReadonlyArray<string>): VoiceStreamWatchContext {
	const removedKeys = new Set(normalizeViewerStreamKeys(keys));
	if (removedKeys.size === 0) return context;
	const viewerStreamKeys = removeManyFromKeys(context.viewerStreamKeys, removedKeys);
	const deferredStopKeys = removeManyFromSet(context.deferredStopKeys, removedKeys);
	if (
		areOrderedStringArraysEqual(context.viewerStreamKeys, viewerStreamKeys) &&
		setsEqual(context.deferredStopKeys, deferredStopKeys)
	) {
		return context;
	}
	return {viewerStreamKeys, deferredStopKeys};
}

function deferRemove(context: VoiceStreamWatchContext, key: string): VoiceStreamWatchContext {
	if (!key || !context.viewerStreamKeys.includes(key) || context.deferredStopKeys.has(key)) return context;
	const deferredStopKeys = new Set(context.deferredStopKeys);
	deferredStopKeys.add(key);
	return {viewerStreamKeys: context.viewerStreamKeys, deferredStopKeys};
}

function cancelDeferredRemove(context: VoiceStreamWatchContext, key: string): VoiceStreamWatchContext {
	if (!context.deferredStopKeys.has(key)) return context;
	const deferredStopKeys = new Set(context.deferredStopKeys);
	deferredStopKeys.delete(key);
	return {
		viewerStreamKeys: context.viewerStreamKeys,
		deferredStopKeys: deferredStopKeys.size === 0 ? EMPTY_SET : deferredStopKeys,
	};
}

const EMPTY_SET: ReadonlySet<string> = new Set();

export const voiceStreamWatchStateMachine = setup({
	types: {} as {
		context: VoiceStreamWatchContext;
		events: VoiceStreamWatchEvent;
		input: VoiceStreamWatchInput;
	},
	actions: {
		replace: assign(({context, event}) =>
			event.type === 'watch.replace' ? replaceContext(context, event.keys) : context,
		),
		add: assign(({context, event}) => (event.type === 'watch.add' ? addKey(context, event.key) : context)),
		remove: assign(({context, event}) => (event.type === 'watch.remove' ? removeKeys(context, [event.key]) : context)),
		removeMany: assign(({context, event}) =>
			event.type === 'watch.removeMany' ? removeKeys(context, event.keys) : context,
		),
		deferRemove: assign(({context, event}) =>
			event.type === 'watch.deferRemove' ? deferRemove(context, event.key) : context,
		),
		cancelDeferredRemove: assign(({context, event}) =>
			event.type === 'watch.cancelDeferredRemove' ? cancelDeferredRemove(context, event.key) : context,
		),
		reset: assign(() => ({viewerStreamKeys: [], deferredStopKeys: EMPTY_SET})),
	},
	guards: {
		hasWatchedStreams: ({context}) => context.viewerStreamKeys.length > 0,
	},
}).createMachine({
	id: 'voiceStreamWatch',
	context: ({input}) => {
		const viewerStreamKeys = normalizeViewerStreamKeys(input.viewerStreamKeys ?? []);
		return {
			viewerStreamKeys,
			deferredStopKeys: normalizeDeferredStopKeys(input.deferredStopKeys, viewerStreamKeys),
		};
	},
	initial: 'routing',
	states: {
		routing: {
			always: [{guard: 'hasWatchedStreams', target: 'watching'}, {target: 'idle'}],
		},
		idle: {
			on: {
				'watch.replace': {target: 'routing', actions: 'replace'},
				'watch.add': {target: 'routing', actions: 'add'},
				'watch.remove': {target: 'routing', actions: 'remove'},
				'watch.removeMany': {target: 'routing', actions: 'removeMany'},
				'watch.deferRemove': {target: 'routing', actions: 'deferRemove'},
				'watch.cancelDeferredRemove': {target: 'routing', actions: 'cancelDeferredRemove'},
				'watch.reset': {target: 'routing', actions: 'reset'},
			},
		},
		watching: {
			on: {
				'watch.replace': {target: 'routing', actions: 'replace'},
				'watch.add': {target: 'routing', actions: 'add'},
				'watch.remove': {target: 'routing', actions: 'remove'},
				'watch.removeMany': {target: 'routing', actions: 'removeMany'},
				'watch.deferRemove': {target: 'routing', actions: 'deferRemove'},
				'watch.cancelDeferredRemove': {target: 'routing', actions: 'cancelDeferredRemove'},
				'watch.reset': {target: 'routing', actions: 'reset'},
			},
		},
	},
});

export type VoiceStreamWatchSnapshot = SnapshotFrom<typeof voiceStreamWatchStateMachine>;
export type VoiceStreamWatchStateValue = 'idle' | 'watching';

export function createVoiceStreamWatchSnapshot(
	viewerStreamKeys: ReadonlyArray<string> = [],
	deferredStopKeys: Iterable<string> = [],
): VoiceStreamWatchSnapshot {
	return getInitialSnapshot(voiceStreamWatchStateMachine, {viewerStreamKeys, deferredStopKeys});
}

export function transitionVoiceStreamWatchSnapshot(
	snapshot: VoiceStreamWatchSnapshot,
	event: VoiceStreamWatchEvent,
): VoiceStreamWatchSnapshot {
	return transition(voiceStreamWatchStateMachine, snapshot, event)[0] as VoiceStreamWatchSnapshot;
}

export function getVoiceStreamWatchStateValue(snapshot: VoiceStreamWatchSnapshot): VoiceStreamWatchStateValue {
	return snapshot.value === 'watching' ? 'watching' : 'idle';
}

export function transitionViewerStreamKeys(
	viewerStreamKeys: ReadonlyArray<string>,
	event: VoiceStreamWatchEvent,
): Array<string> {
	return [
		...transitionVoiceStreamWatchSnapshot(createVoiceStreamWatchSnapshot(viewerStreamKeys), event).context
			.viewerStreamKeys,
	];
}
